import React, { Fragment, useMemo, useEffect, useRef } from "react";
import { Card, Table, Row, Col } from "react-bootstrap";
import moment from "moment-timezone";

const DEPLOYMENT_MAP = {
  "96d3b47b-9a64-4cd7-826b-bc70d34505d8": "Construction",
  "8a25f388-d5ca-42cd-913d-3127c75a95ee": "FA",
  "6652f797-a74c-4af1-be2a-c8d101f25221": "Pricing",
};

// Transform and bucket runs by the hour with exact times
function transformDynamicData(deployments) {
  const today = moment.tz("America/New_York").startOf("day");
  const result = { FA: {}, Construction: {}, Pricing: {} };

  deployments.forEach((deployment) => {
    const colKey = DEPLOYMENT_MAP[deployment.deployment_id];
    if (!colKey) return;

    (deployment.activity_of_runs || []).forEach((run) => {
      const timeString = run.expected_start_time || run.flow_run_start_time;
      const runMoment = moment.tz(timeString, "UTC").tz("America/New_York");
      if (!runMoment.isSame(today, 'day')) return;

      const hourKey = runMoment.clone().startOf('hour').format('h:mm A');
      const displayTime = runMoment.format('h:mm A');

      let status = run.state || 'N/A';
      let runtime = run.run_duration_formatted || '-';
      if (run.expected_start_time && runMoment.isAfter(moment.utc())) {
        status = 'Scheduled';
        runtime = '';
      }

      result[colKey][hourKey] = { status, runtime, displayTime };
    });
  });

  return result;
}

const ClusterPoolOne = ({ dynamicData }) => {
  const data = Array.isArray(dynamicData)
    ? transformDynamicData(dynamicData)
    : { FA: {}, Construction: {}, Pricing: {} };

  const nowEST = moment.tz('America/New_York');
  const currentHour = nowEST.clone().startOf('hour').format('h:mm A');
  const currentRowRef = useRef(null);

  useEffect(() => {
    if (currentRowRef.current) {
      currentRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  // Build fixed hourly slots from 12:00 AM to 11:00 PM
  const timeSlots = useMemo(() => {
    const start = moment.tz('America/New_York').startOf('day');
    return Array.from({ length: 24 }, (_, i) =>
      start.clone().add(i, 'hours').format('h:mm A')
    );
  }, []);

  const columns = [
    { title: 'FA', dataKey: 'FA' },
    { title: 'Pricing', dataKey: 'Pricing' },
    { title: 'Construction', dataKey: 'Construction' },
  ];

  const getStatusStyle = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':   return { color: 'green', fontWeight: 'bold' };
      case 'running':
      case 'in progress': return { color: 'blue', fontWeight: 'bold' };
      case 'failed':
      case 'cancelled':   return { color: 'red', fontWeight: 'bold' };
      case 'scheduled':   return { color: '#FFD700', fontWeight: 'bold' };
      default:            return {};
    }
  };

  function getDataForTime(colKey, slotTime) {
    
    const exact = data[colKey]?.[slotTime];
    if (exact) return exact;

    
    const slotHour = moment(slotTime, 'h:mm A').hour();
    const matchKey = Object.keys(data[colKey] || {}).find(
      (k) => moment(k, 'h:mm A').hour() === slotHour
    );
    if (matchKey) return data[colKey][matchKey];

    
    const slotMoment = moment.tz(
      `${nowEST.format('M/D/YYYY')} ${slotTime}`,
      'M/D/YYYY h:mm A',
      'America/New_York'
    );
    if (slotMoment.isAfter(nowEST)) {
      return { status: 'Scheduled', runtime: '', displayTime: slotTime };
    }

    return { status: '-', runtime: '-', displayTime: slotTime };
  }

  return (
    <Fragment>
      <Row className="mt-5">
        <Col lg={12}>
          <Card style={{ borderRadius: 8, overflow: 'hidden' }}>
            <Card.Header>
              <Card.Title className="text-center" style={{ color: '#3D4465' }}>
                Cluster Pool 1
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <div style={{ maxHeight: 400, overflowY: 'auto', position: 'relative' }}>
                <Table bordered style={{ borderRadius: 8, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.title}
                          colSpan={3}
                          style={{ textAlign: 'center', position: 'sticky', top: 0, backgroundColor: '#3D4465', color: 'white', padding: 8, zIndex: 4 }}
                        >
                          <strong>{col.title}</strong>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {columns.map((col) => (
                        <React.Fragment key={col.title}>
                          <th style={{ textAlign: 'center', position: 'sticky', top: 40, backgroundColor: '#3D4465', color: 'white', padding: 8, zIndex: 3 }}>Run Time</th>
                          <th style={{ textAlign: 'center', position: 'sticky', top: 40, backgroundColor: '#3D4465', color: 'white', padding: 8, zIndex: 3 }}>Status</th>
                          <th style={{ textAlign: 'center', position: 'sticky', top: 40, backgroundColor: '#3D4465', color: 'white', padding: 8, zIndex: 3 }}>Runtime</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time) => {
                      const isCurrent = time === currentHour;
                      return (
                        <tr
                          key={time}
                          ref={isCurrent ? currentRowRef : null}
                          style={isCurrent ? { position: 'sticky', top: 80, backgroundColor: '#d1e7dd', zIndex: 2 } : {}}
                        >
                          {columns.map((col) => {
                            const { status, runtime, displayTime } = getDataForTime(col.dataKey, time);
                            return (
                              <React.Fragment key={`${col.dataKey}-${time}`}>
                                <td style={{ textAlign: 'center' }}>{displayTime}</td>
                                <td style={{ textAlign: 'center' }}><span style={getStatusStyle(status)}>{status === 'Completed' ? 'Succeeded' : status}</span></td>
                                <td style={{ textAlign: 'center' }}>{runtime}</td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default ClusterPoolOne;
